import { DateFilter, MetricData, MetricsResponse } from '@chiroup/core/types/AdminMetrics.type';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  Database,
  HardDrive,
  RefreshCw,
  Server,
  Table as TableIcon,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { cn } from '../../lib/utils';
import Modal from '../common/Modal';
import AnalyticsLoading from './components/AnalyticsLoading';
import DashboardNavigation from './components/DashboardNavigation';
import ModernDatePicker from './components/ModernDatePicker';
import analyticsService from './services/analytics.service';

// Helper functions
const formatNumber = (num: number): string => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;

  return num.toLocaleString();
};

const toNumber = (value: any): number => {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
};

const formatSize = (mb: any): string => {
  const sizeMb = toNumber(mb);
  if (sizeMb >= 1024 * 1024) return `${(sizeMb / (1024 * 1024)).toFixed(2)} TB`;
  if (sizeMb >= 1024) return `${(sizeMb / 1024).toFixed(2)} GB`;

  return `${sizeMb.toFixed(2)} MB`;
};

const getMetricIcon = (metricId: string) => {
  if (metricId.includes('total-size')) return HardDrive;
  if (metricId.includes('total-rows')) return TableIcon;
  if (metricId.includes('table-count')) return Database;
  if (metricId.includes('growth-rate')) return TrendingUp;
  if (metricId.includes('daily-growth')) return TrendingUp;
  if (metricId.includes('daily-size')) return BarChart3;

  return Server;
};

const getMetricIconBackground = (metricId: string) => {
  if (metricId.includes('total-size')) return 'bg-blue-100 dark:bg-blue-900/30';
  if (metricId.includes('total-rows')) return 'bg-green-100 dark:bg-green-900/30';
  if (metricId.includes('table-count')) return 'bg-purple-100 dark:bg-purple-900/30';
  if (metricId.includes('growth-rate')) return 'bg-orange-100 dark:bg-orange-900/30';
  if (metricId.includes('daily-growth')) return 'bg-cyan-100 dark:bg-cyan-900/30';
  if (metricId.includes('daily-size')) return 'bg-pink-100 dark:bg-pink-900/30';

  return 'bg-gray-100 dark:bg-gray-900/30';
};

const getMetricIconColor = (metricId: string) => {
  if (metricId.includes('total-size')) return 'text-blue-600 dark:text-blue-400';
  if (metricId.includes('total-rows')) return 'text-green-600 dark:text-green-400';
  if (metricId.includes('table-count')) return 'text-purple-600 dark:text-purple-400';
  if (metricId.includes('growth-rate')) return 'text-orange-600 dark:text-orange-400';
  if (metricId.includes('daily-growth')) return 'text-cyan-600 dark:text-cyan-400';
  if (metricId.includes('daily-size')) return 'text-pink-600 dark:text-pink-400';

  return 'text-gray-600 dark:text-gray-400';
};

interface KPICardProps {
  metric: MetricData;
  index: number;
}

const KPICard = ({ metric, index }: KPICardProps) => {
  const Icon = getMetricIcon(metric.id);

  const formatValue = () => {
    if (metric.id.includes('total-size') || metric.id.includes('daily-size')) {
      return formatSize(metric.value as any);
    }

    if (metric.displayType === 'percentage') {
      return `${toNumber(metric.value).toFixed(2)}%`;
    }

    return formatNumber(toNumber(metric.value));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-lg transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className={cn('p-2.5 rounded-lg', getMetricIconBackground(metric.id))}>
              <Icon className={cn('w-5 h-5', getMetricIconColor(metric.id))} />
            </div>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.name}</h3>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatValue()}</p>
            {metric.change !== undefined && metric.change !== null && metric.changeType && (
              <span
                className={cn(
                  'text-sm font-semibold flex items-center gap-1',
                  metric.changeType === 'increase'
                    ? 'text-green-600 dark:text-green-400'
                    : metric.changeType === 'decrease'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                )}
              >
                {metric.changeType === 'increase'
                  ? '↑'
                  : metric.changeType === 'decrease'
                    ? '↓'
                    : '→'}
                {metric.id.includes('size')
                  ? formatSize(Math.abs(metric.change))
                  : formatNumber(Math.abs(metric.change))}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{metric.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

interface TableDetailsGridProps {
  data: any[];
  onRowClick?: (tableName: string) => void;
}

const TableDetailsGrid = ({ data, onRowClick }: TableDetailsGridProps) => {
  const [sortField, setSortField] = useState<string>('total_size_mb');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = toNumber(a[sortField]);
      const bVal = toNumber(b[sortField]);

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <TableIcon className="w-5 h-5 text-blue-600" />
          Table Details
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Detailed statistics for all tracked database tables
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
                onClick={() => handleSort('table_name')}
              >
                Table Name
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
                onClick={() => handleSort('total_rows')}
              >
                Rows {sortField === 'total_rows' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
                onClick={() => handleSort('total_size_mb')}
              >
                Size {sortField === 'total_size_mb' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
                onClick={() => handleSort('row_delta')}
              >
                Row Delta {sortField === 'row_delta' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
                onClick={() => handleSort('growth_rate')}
              >
                Growth % {sortField === 'growth_rate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((row, index) => (
              <tr
                key={row.table_name}
                className={cn(
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick && onRowClick(row.table_name)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {row.table_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                  {formatNumber(toNumber(row.total_rows))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                  {formatSize(row.total_size_mb)}
                </td>
                <td
                  className={cn(
                    'px-6 py-4 whitespace-nowrap text-sm text-right font-medium',
                    toNumber(row.row_delta) > 0
                      ? 'text-green-600 dark:text-green-400'
                      : toNumber(row.row_delta) < 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {toNumber(row.row_delta) > 0 ? '+' : ''}
                  {formatNumber(toNumber(row.row_delta || 0))}
                </td>
                <td
                  className={cn(
                    'px-6 py-4 whitespace-nowrap text-sm text-right font-medium',
                    toNumber(row.growth_rate || 0) > 10
                      ? 'text-orange-600 dark:text-orange-400'
                      : toNumber(row.growth_rate || 0) > 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {row.growth_rate !== null ? `${toNumber(row.growth_rate).toFixed(2)}%` : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface TopGrowingTablesProps {
  data: any[];
}

const TopGrowingTables = ({ data }: TopGrowingTablesProps) => {
  const maxDelta = Math.max(...data.map(item => toNumber(item.total_delta || 0)), 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-green-600" />
          Top Growing Tables
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Tables with highest row count growth
        </p>
      </div>
      <div className="space-y-3">
        {data.slice(0, 10).map((item, index) => (
          <div key={item.table_name} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-900 dark:text-white">{item.table_name}</span>
              <div className="flex items-center gap-2">
                <span className="text-green-600 dark:text-green-400 font-semibold">
                  +{formatNumber(toNumber(item.total_delta))}
                </span>
                <span className="text-xs text-gray-500">
                  ({toNumber(item.avg_growth_rate).toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all"
                style={{
                  width: `${maxDelta > 0 ? (toNumber(item.total_delta) / maxDelta) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface TopClinicsProps {
  data: any[];
}

const TopClinics = ({ data }: TopClinicsProps) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-600" />
          Top Clinics by Data Volume (Approx)
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Approximate storage by distributing table size proportionally to clinic rows
        </p>
      </div>
      <div className="space-y-3">
        {data.map(item => (
          <button
            key={item.clinic_id}
            onClick={item.onClick ? () => item.onClick() : undefined}
            className="w-full text-left flex items-center justify-between rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 p-2"
          >
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Clinic {item.clinic_id}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatNumber(toNumber(item.total_rows))} rows
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatSize(item.approx_total_size_mb)}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

interface AnomalyAlertsProps {
  data: any[];
}

const AnomalyAlerts = ({ data }: AnomalyAlertsProps) => {
  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p>No anomalies detected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          Growth Anomalies
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Tables with unusual growth patterns (&gt;50% growth)
        </p>
      </div>
      <div className="space-y-3">
        {data.map(anomaly => (
          <div
            key={`${anomaly.table_name}-${anomaly.clinic_id || 'platform'}`}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {anomaly.table_name}
                  {anomaly.clinic_id && (
                    <span className="ml-2 text-xs text-gray-500">(Clinic {anomaly.clinic_id})</span>
                  )}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {formatNumber(toNumber(anomaly.row_count))} rows
                  {anomaly.row_count_delta && (
                    <span className="ml-2 text-red-600 dark:text-red-400 font-semibold">
                      +{formatNumber(toNumber(anomaly.row_count_delta))}
                    </span>
                  )}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {toNumber(anomaly.growth_rate_percent).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500">growth</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DbStatsDashboard = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'past90days',
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [clinicData, setClinicData] = useState<MetricsResponse | null>(null as any);
  const [isClinicLoading, setIsClinicLoading] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState<string | null>(null);
  const [tableClinicsData, setTableClinicsData] = useState<MetricsResponse | null>(null as any);
  const [isTableClinicsLoading, setIsTableClinicsLoading] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'db-stats', dateFilter],
    () => analyticsService.getMetrics('db-stats', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    }
  );

  // Minimum loading duration (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumDurationComplete(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const shouldShowLoading = isLoading || !minimumDurationComplete;

  // Extract metrics by ID
  const metricsMap = useMemo(() => {
    const map = new Map<string, MetricData>();
    data?.metrics?.forEach(metric => {
      map.set(metric.id, metric);
    });

    return map;
  }, [data?.metrics]);

  const kpiMetrics = useMemo(() => {
    return [
      'db-total-size',
      'db-total-rows',
      'db-table-count',
      'db-growth-rate',
      'db-daily-growth',
      'db-daily-size-growth',
    ]
      .map(id => metricsMap.get(id))
      .filter((m): m is MetricData => m !== undefined);
  }, [metricsMap]);

  const topGrowingData = metricsMap.get('db-top-growing-tables')?.value as any[];
  const tableDetailsData = metricsMap.get('db-table-details')?.value as any[];
  const anomaliesData = metricsMap.get('db-anomalies')?.value as any[];
  const topClinicsData = metricsMap.get('db-top-clinics')?.value as any[];

  const openClinicDetails = async (clinicId: number) => {
    setSelectedClinicId(clinicId);
    setIsClinicLoading(true);

    try {
      const res = await analyticsService.getDbStatsClinic(clinicId, dateFilter);
      const normalized = (res as any)?.metrics
        ? (res as any)
        : (res as any)?.body?.metrics
          ? (res as any).body
          : null;
      setClinicData(normalized);
    } finally {
      setIsClinicLoading(false);
    }
  };

  const closeClinicDetails = () => {
    setSelectedClinicId(null);
    setClinicData(null);
  };

  const openTableClinics = async (tableName: string) => {
    setSelectedTableName(tableName);
    setIsTableClinicsLoading(true);

    try {
      const res = await analyticsService.getDbStatsTableClinics(tableName, dateFilter);
      setTableClinicsData(res);
    } finally {
      setIsTableClinicsLoading(false);
    }
  };

  const closeTableClinics = () => {
    setSelectedTableName(null);
    setTableClinicsData(null);
  };

  if (shouldShowLoading) {
    return <AnalyticsLoading />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <DashboardNavigation currentDashboardId="db-stats" />
        <div className="p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-red-700 dark:text-red-300 mb-4">
              {(error as Error).message || 'Failed to load database statistics'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <DashboardNavigation currentDashboardId="db-stats" />

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white"
      >
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative px-6 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                    <Server className="w-8 h-8" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold">Database Statistics Dashboard</h1>
                    <p className="text-blue-100 mt-1">
                      Monitor database growth, table sizes, and capacity planning metrics
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ModernDatePicker
                  selected={
                    dateFilter.type === 'custom' && dateFilter.endDate
                      ? (() => {
                          // Parse date in local timezone to avoid off-by-one errors
                          const [year, month, day] = dateFilter.endDate.split('-').map(Number);

                          return new Date(year, month - 1, day);
                        })()
                      : null
                  }
                  onChange={date => {
                    if (date) {
                      // Format date in local timezone
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const ymd = `${year}-${month}-${day}`;
                      setDateFilter({ type: 'custom', startDate: ymd, endDate: ymd });
                    } else {
                      setDateFilter({ type: 'past90days' });
                    }
                  }}
                />
                <button
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {kpiMetrics.map((metric, index) => (
            <KPICard key={metric.id} metric={metric} index={index} />
          ))}
        </div>

        {/* Top Clinics - full width */}
        {topClinicsData && topClinicsData.length > 0 && (
          <TopClinics
            data={topClinicsData.map(item => ({
              ...item,
              onClick: () => openClinicDetails(item.clinic_id),
            }))}
          />
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {topGrowingData && topGrowingData.length > 0 && (
            <TopGrowingTables data={topGrowingData} />
          )}
          {anomaliesData && <AnomalyAlerts data={anomaliesData} />}
        </div>

        {/* Clinic details modal */}
        {selectedClinicId !== null && (
          <Modal
            isOpen={selectedClinicId !== null}
            close={closeClinicDetails}
            header={{ title: `Clinic ${selectedClinicId} Details` }}
            size="lg"
            darkBg
          >
            {isClinicLoading ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
            ) : clinicData ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Overview
                  </h4>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex items-center justify-between py-1">
                      <span>Total Rows</span>
                      <span className="font-semibold">
                        {formatNumber(
                          Number(
                            (clinicData.metrics.find(m => m.id === 'clinic-overview')?.value as any)
                              ?.total_rows || 0
                          )
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span>Approx Size</span>
                      <span className="font-semibold">
                        {formatSize(
                          Number(
                            (clinicData.metrics.find(m => m.id === 'clinic-overview')?.value as any)
                              ?.approx_total_size_mb || 0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Growth</h4>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex items-center justify-between py-1">
                      <span>Last 30 days</span>
                      <span className="font-semibold">
                        {formatNumber(
                          Number(
                            (
                              clinicData.metrics.find(m => m.id === 'clinic-row-growth')
                                ?.value as any
                            )?.d30_row_delta || 0
                          )
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span>Last 90 days</span>
                      <span className="font-semibold">
                        {formatNumber(
                          Number(
                            (
                              clinicData.metrics.find(m => m.id === 'clinic-row-growth')
                                ?.value as any
                            )?.d90_row_delta || 0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 bg-white dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Top Tables (Approx Size)
                  </h4>
                  <div className="space-y-2">
                    {(
                      (clinicData.metrics.find(m => m.id === 'clinic-top-tables')
                        ?.value as any[]) || []
                    ).map(row => (
                      <div key={row.table_name} className="flex items-center justify-between">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {row.table_name}
                          <span className="ml-2 text-xs text-gray-500">
                            {formatNumber(Number(row.clinic_rows))} rows
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatSize(Number(row.approx_mb))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">No data</div>
            )}
          </Modal>
        )}

        {/* Table Details Grid */}
        {tableDetailsData && tableDetailsData.length > 0 && (
          <TableDetailsGrid data={tableDetailsData} onRowClick={openTableClinics} />
        )}

        {selectedTableName && (
          <Modal
            isOpen={!!selectedTableName}
            close={closeTableClinics}
            header={{ title: `${selectedTableName} • Clinic Breakdown` }}
            size="lg"
            darkBg
          >
            {isTableClinicsLoading ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
            ) : tableClinicsData ? (
              <div className="space-y-3">
                {(
                  (tableClinicsData.metrics.find(m => m.id === 'db-table-clinic-breakdown')
                    ?.value as any[]) || []
                ).map(row => (
                  <div
                    key={row.clinic_id}
                    className="flex items-center justify-between rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Clinic {row.clinic_id}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatNumber(Number(row.clinic_rows))} rows
                      </p>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatSize(Number(row.approx_mb))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">No data</div>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default DbStatsDashboard;
